import React, { useEffect, useState } from 'react';
import { FileSearch, Archive, Wrench, Lock } from 'lucide-react';
import FormControl from '../common/FormControl';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Toast, { type ToastMessage } from '../common/Toast';
import { capaService, CapaIssue, CreateActionPayload } from '../../services/capaService';
import { capaInvestigationsService } from '../../services/capaInvestigationsService';
import { userService, User } from '../../services/userService';
import { filterPickableUsers } from '../../utils/userPickable';
import { usePermissions } from '../../contexts/PermissionContext';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';
import { maxLenForCode } from '../../validation/engine';
import extractApiError from '../../utils/apiError';

const userLabel = (u: User) => {
    const name = `${u.first_name || ''} ${u.last_name || ''}`.trim();
    if (name && u.login_id) return `${name} (${u.login_id})`;
    return name || u.login_id || `#${u.id}`;
};

const PRIORITY_OPTIONS = [
    { value: 'LOW', label: 'Low' },
    { value: 'MEDIUM', label: 'Medium' },
    { value: 'HIGH', label: 'High' },
    { value: 'CRITICAL', label: 'Critical' },
];

const TRIAGE_LABELS: Record<string, string> = {
    TRIVIAL: 'Trivial',
    INVESTIGATION: 'In Investigation',
    ACTION_ONLY: 'Action Only',
};

type Mode = 'initial' | 'investigation' | 'trivial' | 'task';

interface Props {
    isOpen: boolean;
    issue: CapaIssue | null;
    onClose: () => void;
    /** Called after a triage path succeeds. */
    onUpdated: () => void;
}

const IssueTriageModal: React.FC<Props> = ({ isOpen, issue, onClose, onUpdated }) => {
    // Preserve the last valid issue so the body stays populated during the
    // close animation (same trick as IncidentTriageModal).
    const lastIssueRef = React.useRef<CapaIssue | null>(issue);
    if (issue) lastIssueRef.current = issue;
    const activeIssue = lastIssueRef.current;

    const { check } = usePermissions();
    const { formatDate } = useEntitySettings();
    const canTriage = check('CAP_ISSUE_TRIAGE', 'view+modify').allowed;
    const isTriaged = !!activeIssue?.triage_outcome;

    const [users, setUsers] = useState<User[]>([]);
    const [mode, setMode] = useState<Mode>('initial');
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    // Investigation path
    const [ownerId, setOwnerId] = useState<number | null>(null);
    const [invDueDate, setInvDueDate] = useState('');
    // Trivial path
    const [trivialNote, setTrivialNote] = useState('');
    // Task path
    const [task, setTask] = useState<CreateActionPayload>({ name: '' });

    const fieldVh = useFieldValidation();

    useEffect(() => {
        if (!isOpen) return;
        // Reset to a clean slate every time the modal opens for a row.
        setMode('initial');
        setToast(null);
        setOwnerId(null);
        setInvDueDate('');
        setTrivialNote('');
        setTask({ name: '' });
        fieldVh.reset();
        userService.getAllUsers({ limit: 200 }).then(r => setUsers(filterPickableUsers(r.data || []))).catch(() => {});
        // `fieldVh` is intentionally NOT a dependency: useFieldValidation returns a
        // fresh object every render, so including it would re-run this effect on
        // every render and force setMode('initial') — silently undoing every card
        // click. This effect must fire only when the modal opens or the row changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, activeIssue?.id]);

    // Clear stale inline errors when the triage path changes.
    useEffect(() => { fieldVh.reset(); setToast(null); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [mode]);

    const userOptions = users.map(u => ({ value: u.id, label: userLabel(u) }));

    const handlePromote = async () => {
        if (!activeIssue) return;
        if (!ownerId) { setToast({ type: 'error', message: 'Owner is required' }); return; }
        setToast(null); setSubmitting(true);
        try {
            await capaInvestigationsService.sendToInvestigation(activeIssue.id, {
                owner_user_id: ownerId,
                due_date: invDueDate || null,
            });
            onUpdated();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to create investigation') });
        } finally { setSubmitting(false); }
    };

    const handleTrivial = async () => {
        if (!activeIssue) return;
        setToast(null); setSubmitting(true);
        try {
            await capaService.setTriage(activeIssue.id, 'TRIVIAL');
            onUpdated();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to mark trivial') });
        } finally { setSubmitting(false); }
    };

    const handleTask = async () => {
        if (!activeIssue) return;
        const valid = fieldVh.validateAll([
            { name: 'name', code: 109, value: task.name, required: true },
            { name: 'description', code: 110, value: task.description },
            { name: 'due_date', code: 305, value: task.due_date },
        ]);
        if (!valid) return;
        setToast(null); setSubmitting(true);
        try {
            await capaService.createAction(activeIssue.id, task);
            onUpdated();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to create action') });
        } finally { setSubmitting(false); }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" closeOnOverlayClick={false}>
            <Modal.Header>
                Triage Issue - {activeIssue?.issue_code}
            </Modal.Header>
            <Modal.Body>
                <div className="flex gap-0 -mx-3 -mt-3">
                    {/* Left panel — issue details */}
                    <div className="w-72 flex-shrink-0 bg-gray-50 border-r border-gray-200 p-6 overflow-y-auto">
                        <h6 className="uppercase text-xs font-bold text-gray-400 mb-4 tracking-wider">CAPA Issue</h6>
                        <div className="space-y-6">
                            <div>
                                <p className="text-xs text-gray-400 mb-1">Code</p>
                                <p className="text-sm font-medium text-gray-700">{activeIssue?.issue_code || '-'}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-400 mb-1">Issue</p>
                                <p className="text-sm font-medium text-gray-700">{activeIssue?.issue_name || '-'}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-400 mb-1">Description</p>
                                <p className="text-sm text-gray-700 leading-relaxed">{activeIssue?.issue_description || '—'}</p>
                            </div>
                            {activeIssue?.target_date && (
                                <div>
                                    <p className="text-xs text-gray-400 mb-1">Target Date</p>
                                    <p className="text-sm text-gray-700">{formatDate(activeIssue.target_date)}</p>
                                </div>
                            )}
                            <div>
                                <p className="text-xs text-gray-400 mb-1">Source</p>
                                <p className="text-sm text-gray-700">{activeIssue?.issue_source_type || 'Manual'}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-400 mb-1">Triage Status</p>
                                <p className="text-sm text-gray-700">
                                    {activeIssue?.triage_outcome ? (TRIAGE_LABELS[activeIssue.triage_outcome] ?? activeIssue.triage_outcome) : 'Untriaged'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Right panel — triage workbench */}
                    <div className="flex-1 p-6 overflow-y-auto">
                        <h6 className="uppercase text-xs font-bold text-gray-400 mb-4 tracking-wider">Triage Decision Workbench</h6>

                        {!canTriage && (
                            <div className="mb-4 p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-700 text-sm flex items-center gap-2">
                                <Lock size={14} /> You do not have permission to triage CAPA issues. Contact your administrator.
                            </div>
                        )}

                        {canTriage && isTriaged && (
                            <div className="mb-4 p-3 rounded-md bg-gray-50 border border-gray-200 text-gray-600 text-sm flex items-center gap-2">
                                <Lock size={14} /> This issue has already been triaged
                                ({TRIAGE_LABELS[activeIssue!.triage_outcome!] ?? activeIssue!.triage_outcome}). No further triage decision is available.
                            </div>
                        )}

                        {canTriage && !isTriaged && (
                            <div className="flex gap-3 mb-6">
                                <button
                                    type="button"
                                    onClick={() => setMode('investigation')}
                                    className={`flex-1 p-4 text-left border rounded-lg transition-all duration-200 ${mode === 'investigation' ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'}`}
                                    data-testid="BTN_capa_triage_mode_investigation"
                                >
                                    <div className="flex items-center gap-2 font-semibold text-sm mb-1"><FileSearch size={16} className="text-blue-600" />Promote to Investigation</div>
                                    <p className="text-xs text-gray-500">Open an investigation &amp; assign a head.</p>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setMode('trivial')}
                                    className={`flex-1 p-4 text-left border rounded-lg transition-all duration-200 ${mode === 'trivial' ? 'border-green-500 bg-green-50 ring-1 ring-green-500' : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'}`}
                                    data-testid="BTN_capa_triage_mode_trivial"
                                >
                                    <div className="flex items-center gap-2 font-semibold text-sm mb-1"><Archive size={16} className="text-green-600" />Drop as Trivial</div>
                                    <p className="text-xs text-gray-500">Archive with no further action.</p>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setMode('task')}
                                    className={`flex-1 p-4 text-left border rounded-lg transition-all duration-200 ${mode === 'task' ? 'border-amber-500 bg-amber-50 ring-1 ring-amber-500' : 'border-gray-200 hover:border-amber-300 hover:bg-gray-50'}`}
                                    data-testid="BTN_capa_triage_mode_task"
                                >
                                    <div className="flex items-center gap-2 font-semibold text-sm mb-1"><Wrench size={16} className="text-amber-600" />Convert to Task</div>
                                    <p className="text-xs text-gray-500">Create a single remedial task.</p>
                                </button>
                            </div>
                        )}

                        {canTriage && !isTriaged && mode === 'initial' && (
                            <p className="text-sm text-gray-400 italic text-center mt-8">Please select a triage path above to proceed.</p>
                        )}

                        {canTriage && !isTriaged && mode === 'investigation' && (
                            <div className="space-y-4">
                                <div className="bg-blue-50 border border-blue-100 text-blue-800 px-4 py-3 rounded text-sm flex items-center">
                                    <FileSearch size={16} className="mr-2 flex-shrink-0" /> This opens an investigation. The assigned head can then build out impacts, root causes and remedial actions.
                                </div>
                                <FormControl.SearchSelect
                                    label="Investigative Head (Owner)"
                                    required
                                    options={userOptions}
                                    value={ownerId}
                                    onChange={v => setOwnerId(v == null ? null : Number(v))}
                                    placeholder="— pick an owner —"
                                    clearable
                                    data-testid="SEL_capa_triage_investigation_owner"
                                />
                                <FormControl.DatePicker
                                    label="Due Date (optional)"
                                    value={invDueDate}
                                    onChange={e => setInvDueDate(e.target.value)}
                                    data-testid="INP_capa_triage_investigation_due_date"
                                />
                            </div>
                        )}

                        {canTriage && !isTriaged && mode === 'trivial' && (
                            <div className="space-y-4">
                                <div className="bg-green-50 border border-green-100 text-green-800 px-4 py-3 rounded text-sm flex items-center">
                                    <Archive size={16} className="mr-2 flex-shrink-0" /> This archives the issue and hides it from the open queue. You can unmark it later from the Archived tab.
                                </div>
                                <FormControl.Textarea
                                    label="Note (optional)"
                                    rows={3}
                                    placeholder="Why is this trivial? (for the record)"
                                    value={trivialNote}
                                    onChange={e => setTrivialNote(e.target.value)}
                                    maxLength={maxLenForCode(110)}
                                    data-testid="INP_capa_triage_trivial_note"
                                />
                            </div>
                        )}

                        {canTriage && !isTriaged && mode === 'task' && (
                            <div className="space-y-4">
                                <div className="bg-amber-50 border border-amber-100 text-amber-800 px-4 py-3 rounded text-sm flex items-center">
                                    <Wrench size={16} className="mr-2 flex-shrink-0" /> Creates a single remedial task linked to this issue without opening a full investigation.
                                </div>
                                <FormControl.Input
                                    label="Task Name"
                                    required
                                    value={task.name}
                                    onChange={e => { setTask(f => ({ ...f, name: e.target.value })); fieldVh.clearError('name'); }}
                                    onBlur={() => fieldVh.validateField('name', 109, task.name, { required: true })}
                                    maxLength={maxLenForCode(109)}
                                    error={fieldVh.errors.name}
                                    data-testid="INP_capa_triage_task_name"
                                />
                                <FormControl.Textarea
                                    label="Description (optional)"
                                    rows={3}
                                    value={task.description || ''}
                                    onChange={e => { setTask(f => ({ ...f, description: e.target.value })); fieldVh.clearError('description'); }}
                                    onBlur={() => fieldVh.validateField('description', 110, task.description || '')}
                                    maxLength={maxLenForCode(110)}
                                    error={fieldVh.errors.description}
                                    data-testid="INP_capa_triage_task_description"
                                />
                                <div className="grid grid-cols-3 gap-x-4 gap-y-4">
                                    <FormControl.SearchSelect
                                        label="Doer"
                                        options={userOptions}
                                        value={task.doer_id ?? null}
                                        onChange={v => setTask(f => ({ ...f, doer_id: v == null ? null : Number(v) }))}
                                        placeholder="(default: you)"
                                        clearable
                                        data-testid="SEL_capa_triage_task_doer"
                                    />
                                    <FormControl.SearchSelect
                                        label="Validator"
                                        options={userOptions}
                                        value={task.validator_id ?? null}
                                        onChange={v => setTask(f => ({ ...f, validator_id: v == null ? null : Number(v) }))}
                                        placeholder="Optional"
                                        clearable
                                        data-testid="SEL_capa_triage_task_validator"
                                    />
                                    <FormControl.SearchSelect
                                        label="Approver"
                                        options={userOptions}
                                        value={task.approver_id ?? null}
                                        onChange={v => setTask(f => ({ ...f, approver_id: v == null ? null : Number(v) }))}
                                        placeholder="Optional"
                                        clearable
                                        data-testid="SEL_capa_triage_task_approver"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                                    <FormControl.DatePicker
                                        label="Due Date"
                                        value={task.due_date || ''}
                                        onChange={e => { setTask(f => ({ ...f, due_date: e.target.value })); fieldVh.clearError('due_date'); }}
                                        onBlur={() => fieldVh.validateField('due_date', 305, task.due_date || '')}
                                        error={fieldVh.errors.due_date}
                                        data-testid="INP_capa_triage_task_due_date"
                                    />
                                    <FormControl.SearchSelect
                                        label="Priority"
                                        options={PRIORITY_OPTIONS}
                                        value={task.priority ?? null}
                                        onChange={v => setTask(f => ({ ...f, priority: (v as any) ?? null }))}
                                        placeholder="Optional"
                                        clearable
                                        data-testid="SEL_capa_triage_task_priority"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                {canTriage && !isTriaged && mode === 'investigation' && (
                    <Button variant="fill" type="button" onClick={handlePromote} disabled={submitting || !ownerId} data-testid="BTN_capa_triage_submit_investigation">
                        {submitting ? 'Submitting...' : 'Create Investigation'}
                    </Button>
                )}
                {canTriage && !isTriaged && mode === 'trivial' && (
                    <Button variant="fill" type="button" onClick={handleTrivial} disabled={submitting} data-testid="BTN_capa_triage_submit_trivial">
                        {submitting ? 'Submitting...' : 'Confirm & Archive'}
                    </Button>
                )}
                {canTriage && !isTriaged && mode === 'task' && (
                    <Button variant="fill" type="button" onClick={handleTask} disabled={submitting} data-testid="BTN_capa_triage_submit_task">
                        {submitting ? 'Submitting...' : 'Create Task'}
                    </Button>
                )}
            </Modal.Footer>
        <Toast toast={toast} onClose={() => setToast(null)} />
        </Modal>
    );
};

export default IssueTriageModal;
